/**
 * DSD 워크북 → IXD 값 반영 버튼.
 *
 * 어느 셀에 어떤 값이 들어갈지는 백엔드의 값 동기화 plan이 정한다. 프론트는
 * 셀의 XBRL identity로 plan을 조회해 확정된 저장값을 편집 세션에 넣는 일만 한다.
 *
 * 화면의 표 모양은 보지 않는다 — 좌우 구조가 달라도 태깅이 맞는 셀은 채워지고,
 * 모양이 같아도 태깅이 다르면 채우지 않는다. 위치로 짝짓던 이전 방식은 모양이
 * 같고 행 순서만 다른 표에서 값을 엉뚱한 행에 넣을 수 있었다.
 *
 * plan의 value는 서버가 단위까지 적용해 확정한 저장값이라 표시값 정규화나
 * 단위 환산을 다시 태우지 않는다. 적용은 수동 붙여넣기와 같은 편집 세션 경로
 * (setAppliedCellValues)를 타서 undo 그룹핑·연동 셀 충돌 처리가 동일하다.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import {
  prepareIxdStoredValues,
  type CellEditBatchEntry,
  type IxdStoredValueEntry,
} from "@/components/ixd-preview";
import type { useIxdEditSession } from "@/hooks/ixd-edit-session";
import { cn } from "@/lib/utils";
import { ATTR } from "@/utils/dom-attrs";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import {
  type MixedScaleLineItemRow,
  getUnitLabel,
} from "@/utils/ixd-editor/unit-conversion";
import { isNumericKind } from "@/utils/value-conversion";
import {
  getIxdSchemaValueKind,
  normalizeEditedCellValue,
  validateStoredCellValue,
} from "@/utils/ixd-editor/value-validation";
import type { AppliedCellValueSnapshot } from "./paste-feedback";
import type { PlanAvailability, PlanValueIndex } from "./plan-value-index";
import { AlignedGrid, ValueGrid } from "./total-aware-alignment";
import { toastApplyResult } from "@/components/common/apply-result-toast";
import { useIsWorkbookPlanRefreshing } from "./use-workbook-auto-sum";
import {
  type PeriodEndNotice,
  PeriodEndNoticeKind,
} from "./period-end-mismatch";
import { logIxdEditorAction } from "@/utils/usage-log";

type PasteMatchSession = Pick<
  ReturnType<typeof useIxdEditSession>,
  "pushUndoMarker" | "setAppliedCellValues"
>;

export interface PasteMatchResult {
  failedCellUniqueIds: string[];
  /** 실패 당시 셀 저장값. 이후 사용자가 직접 값을 바꿨는지 판정한다. */
  failedCellRawValues: Record<string, string>;
  /** 반영에 성공한 셀의 반영 직후 저장값. 버튼 잠금 판정에 쓴다. */
  appliedCellSnapshot: AppliedCellValueSnapshot;
  /** 반영에 성공한 셀의 identity 키 */
  appliedIdentityKeys: string[];
}

export type PasteMatchPlanStatus =
  /** plan을 기다리는 중이거나 표가 아직 그려지지 않음 */
  | "checking"
  /** 값이 들어갈 셀 자체가 없는 블록(문장영역 등) — 버튼을 감춘다 */
  | "no-cells"
  /** plan을 받지 못함 — 자동 반영 자체가 불가 */
  | "plan-error"
  /** plan에 이 표의 셀과 맞는 태깅이 없음 */
  | "no-match"
  /**
   * 이 표의 계획에는 항목이 있는데 대응 셀을 하나도 못 찾음.
   *
   * 조인 키 한쪽이 빈 문자열일 때 이렇게 된다 — 이 API는 항상 정규형("KO",
   * "Consolidated")을 내보내지만 셀 metadata는 업로드된 V.xml 원문을 그대로
   * 싣기 때문이다. 조용히 "대응 셀 없음"으로 보이면 원인을 찾을 수 없으므로
   * 따로 세어 드러낸다.
   */
  | "join-failed"
  /** 맞는 셀이 있으나 값이 전부 이미 같음 */
  | "values-match"
  /** 넣을 값이 있음 */
  | "ready";

/** 이 표에 대한 plan 적용 가능 범위. 버튼 활성과 안내 문구가 모두 여기서 나온다. */
export interface PasteMatchPlanCoverage {
  status: PasteMatchPlanStatus;
  /** 값이 실제로 바뀔 셀 수 */
  changedCount: number;
  /** 그중 서버가 추정으로 확정한 값의 수. 감추면 "전부 태그로 맞췄다"는 틀린 단언이 된다 */
  inferredCount: number;
  /** 추정 중에서도 표 안 위치로만 찍은 수 — 사람이 봐야 하는 것들 */
  ordinalCount: number;
  /** 이 표 계획에 있는데 대응 셀을 못 찾은 항목 수 */
  unmatchedPlanCount: number;
}

/** 측정 전 초기값. 소비처가 같은 리터럴을 복제하지 않게 내보낸다. */
export const INITIAL_PLAN_COVERAGE: PasteMatchPlanCoverage = {
  status: "checking",
  changedCount: 0,
  inferredCount: 0,
  ordinalCount: 0,
  unmatchedPlanCount: 0,
};

interface PasteMatchButtonProps {
  /** 우측 IXD 표 렌더러 컨테이너 (값이 들어갈 대상) */
  targetContainerRef: React.RefObject<HTMLDivElement>;
  session: PasteMatchSession;
  plan: PlanAvailability;
  /** 이 행이 보여주는 워크북 표 id — 계획에서 이 표의 값을 찾는 키 */
  workbookTableId?: string;
  /** 그 표 안에서 이 행이 보여주는 role — 자동합계 갱신 중 판정에만 쓴다 */
  workbookRoleKey?: string;
  onPlanCoverageChange?: (coverage: PasteMatchPlanCoverage) => void;
  onPasteResult?: (result: PasteMatchResult) => void;
  /**
   * 잠금 소유권은 부모에게 있다 — 이 버튼이 속한 행은 화면 밖에서 unmount된다.
   * 잠그는 쪽(snapshot)과 푸는 쪽(onRelease)이 갈라지면 버튼이 영구히 잠기므로
   * 한 덩어리로 받아 계약을 컴파일 타임에 강제한다. snapshot null이면 해제 상태.
   */
  applyLock: {
    snapshot: AppliedCellValueSnapshot | null;
    onRelease: () => void;
  };
  /**
   * 이 표에서 반영하지 못하는 것들 — 사유별 한 줄씩.
   *
   * 버튼을 누르기 전에 알려준다. 눌러 보고 나서 "왜 몇 개가 안 들어갔지"를
   * 되짚는 것보다 누르기 전에 아는 편이 낫다.
   */
  notAppliedSummary?: readonly string[];
  /**
   * 위 요약과 같은 진단의 건수(PlanTableDiagnostics.notAppliedCount).
   *
   * 서버가 IXD 에서 자리를 못 찾아 계획에서 아예 빠진 셀이라 반영 시도조차
   * 하지 않는다 — 반영 결과 토스트가 "모두 반영"을 말해도 되는지 가른다.
   */
  notAppliedCount?: number;
  /**
   * 같은 외화가 다른 배율로 섞인 표면 반영 결과 토스트에 함께 알린다.
   * 반영 직후가 채워진 값을 행별 단위로 확인할 시점이다. 없으면 null.
   */
  mixedScaleWarning?: MixedScaleWarningSummary | null;
  /** 같은 표의 DSD 워크북과 IXD 단위가 다른 축 — 사유별 한 줄씩(getWorkbookIxdUnitMismatches) */
  unitMismatchLines?: readonly string[];
  /**
   * DSD 워크북과 IXD의 기말 시점 표/기간 표 안내(getWorkbookIxdPeriodEndNotice). 툴팁에는
   * 둘 다 싣지만 주의 점은 불일치(MISMATCH)에만 단다 — 판정 못 함(UNKNOWN)은 틀렸다는 근거가 없다.
   */
  periodEndNotice?: PeriodEndNotice | null;
  /** DSD 워크북과 IXD의 3개월·누적 열 구성이 다르면 맞추는 방법(getThreeMonthAccMismatch) */
  threeMonthAccMismatch?: string | null;
  /** DSD 워크북과 IXD의 기간(당기·전기) 구성이 다르면 그 안내(getTaggedPeriodMismatchMessage) */
  taggedPeriodMismatch?: string | null;
}

export interface MixedScaleWarningSummary {
  /** getMixedScaleWarningText 결과 — 캡션과 같은 문장 */
  text: string;
  /** 배율별 태그 목록 (getMixedScaleLineItemRows 결과) */
  rows: MixedScaleLineItemRow[];
}

/** 반영 결과 토스트 설명 — 추정값 안내와 외화 배율 혼재 경고를 줄로 쌓는다. */
const buildApplyToastDescription = (
  detail: string,
  mixedScaleWarning: MixedScaleWarningSummary | null | undefined,
): React.ReactNode => {
  if (!mixedScaleWarning) return detail || undefined;
  return (
    <div className="flex flex-col gap-1">
      {detail && <span>{detail}</span>}
      <span
        data-mixed-foreign-unit-warning
        className="flex items-center gap-1 text-status-caution"
      >
        <TriangleAlert className="size-3.5 shrink-0" />
        {mixedScaleWarning.text}
      </span>
      {mixedScaleWarning.rows.map(({ key, label, lineItems }) => (
        <span key={key}>
          {label}: {lineItems}
        </span>
      ))}
    </div>
  );
};

/**
 * 결과 처리에 필요한 최소 셀 참조.
 */
type ApplyEntryRef = { uniqueId: string; element: HTMLElement };

/** Figma Icon / ArrowBigLeftDash — 워크북(우) 값을 IXD(좌)로 옮긴다는 방향을 그대로 쓴다 */
const ValueApplyIcon = () => (
  <svg
    aria-hidden
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className="size-4 shrink-0"
  >
    <path
      d="M7.57552 2.90885C7.74712 2.73726 8.00561 2.68643 8.22982 2.7793C8.45386 2.87223 8.60026 3.09076 8.60026 3.33333V5.39974H10C10.3314 5.39974 10.6003 5.66863 10.6003 6V10C10.6003 10.3314 10.3314 10.6003 10 10.6003H8.60026V12.6667C8.60026 12.9092 8.45386 13.1278 8.22982 13.2207C8.00561 13.3136 7.74712 13.2627 7.57552 13.0911L2.90885 8.42448C2.67454 8.19016 2.67454 7.80984 2.90885 7.57552L7.57552 2.90885ZM4.18229 8L7.39974 11.2174V10C7.39974 9.66863 7.66863 9.39974 8 9.39974H9.39974V6.60026H8C7.66863 6.60026 7.39974 6.33137 7.39974 6V4.78255L4.18229 8ZM12.0664 10V6C12.0664 5.66863 12.3353 5.39974 12.6667 5.39974C12.998 5.39974 13.2669 5.66863 13.2669 6V10C13.2669 10.3314 12.998 10.6003 12.6667 10.6003C12.3353 10.6003 12.0664 10.3314 12.0664 10Z"
      fill="currentColor"
    />
  </svg>
);

/** 값이 들어갈 수 있는 셀 전부. sticky 헤더 복제 표는 같은 셀의 사본이라 제외한다. */
const EMPTY_PLANNED_VALUES: PlanValueIndex = new Map();

const queryValueCells = (targetRoot: HTMLElement) =>
  targetRoot.querySelectorAll<HTMLTableCellElement>(
    "table:not([data-sticky-header-clone]) td[data-cell-id][id]",
  );

/**
 * plan이 값을 확정해 둔 셀만 골라낸다.
 *
 * 격자 모양을 보지 않고 셀의 identity만 보므로, 행·열 수가 달라도 태깅이 맞는
 * 셀은 짝지어진다.
 */
const collectPlanEntries = (
  targetRoot: HTMLElement,
  /** 이 표가 계획한 (identity → 값). 다른 표의 계획은 애초에 들어 있지 않다 */
  plannedValues: PlanValueIndex,
): IxdStoredValueEntry[] => {
  const entries: IxdStoredValueEntry[] = [];
  queryValueCells(targetRoot).forEach((cell) => {
    const identityKey = cell.getAttribute(ATTR.cellIdentityKey);
    if (!identityKey) return;
    const storedValue = plannedValues.get(identityKey);
    if (storedValue === undefined) return;
    entries.push({
      cellId: cell.getAttribute("data-cell-id") ?? "",
      uniqueId: cell.id,
      element: cell,
      storedValue,
    });
  });
  return entries;
};

/**
 * 버튼 활성과 안내 문구의 단일 근거.
 *
 * 이미 같은 값인 셀은 changedCount에서 뺀다 — 버튼을 켜 놓고 눌렀더니
 * "변경할 값이 없습니다"가 뜨는 상태를 만들지 않기 위해서다.
 */
export interface PlanCoverageMeasurement {
  coverage: PasteMatchPlanCoverage;
  /** 이번 반영으로 값이 바뀔 셀. 누르기 전 미리보기 강조에 쓴다 */
  changedCellUniqueIds: string[];
}

export const measurePlanCoverage = (
  targetRoot: HTMLElement,
  plan: PlanAvailability,
  workbookTableId: string | undefined,
): PlanCoverageMeasurement => {
  const nothing = (coverage: PasteMatchPlanCoverage) => ({
    coverage,
    changedCellUniqueIds: [],
  });
  if (plan.status === "loading") return nothing(INITIAL_PLAN_COVERAGE);

  // plan이 준비된 뒤에도 값 셀이 없다면 채울 것이 없는 블록이다. plan을 아직
  // 기다리는 동안은 위에서 "checking"으로 빠지므로, 표가 늦게 그려지는 경우와
  // 애초에 표가 없는 경우가 섞이지 않는다.
  const valueCellCount = queryValueCells(targetRoot).length;
  if (valueCellCount === 0) {
    return nothing({ ...INITIAL_PLAN_COVERAGE, status: "no-cells" });
  }

  if (plan.status === "error") {
    return nothing({ ...INITIAL_PLAN_COVERAGE, status: "plan-error" });
  }

  const { confidenceByIdentityKey } = plan.index;
  // 보고 있는 표의 계획만 쓴다. 대응 표를 모르면 넣을 곳도 정할 수 없다.
  const plannedValues = workbookTableId
    ? (plan.index.valuesByWorkbookTableId.get(workbookTableId) ??
      EMPTY_PLANNED_VALUES)
    : EMPTY_PLANNED_VALUES;
  const entries = collectPlanEntries(targetRoot, plannedValues);
  // 셀과 짝지어지는 단위는 entry가 아니라 identity다 — 같은 fact가 여러 entry로
  // 와도 셀은 하나이므로, entry 수로 빼면 연결 실패 건수가 부풀려진다.
  const planEntryCount = plannedValues.size;
  const unmatchedPlanCount = Math.max(0, planEntryCount - entries.length);

  if (entries.length === 0) {
    return nothing({
      ...INITIAL_PLAN_COVERAGE,
      // 계획에는 항목이 있는데 하나도 못 이었다면 태깅이 없는 게 아니라
      // 조인이 깨진 것이다. 두 상황은 사용자에게 다르게 말해야 한다.
      status: planEntryCount > 0 ? "join-failed" : "no-match",
      unmatchedPlanCount,
    });
  }

  const prepared = prepareIxdStoredValues(entries);
  const changed = prepared.batch;
  // 추정 표시는 "이번에 값이 들어가는 셀" 기준이다. 이미 같은 값이라 건드리지
  // 않는 셀까지 세면 사용자가 확인해야 할 양을 부풀린다.
  let inferredCount = 0;
  let ordinalCount = 0;
  for (const { identityKey } of changed) {
    const confidence = identityKey
      ? confidenceByIdentityKey.get(identityKey)
      : undefined;
    if (!confidence) continue;
    inferredCount += 1;
    if (confidence === "ordinal") ordinalCount += 1;
  }

  return {
    coverage: {
      status: changed.length > 0 ? "ready" : "values-match",
      changedCount: changed.length,
      inferredCount,
      ordinalCount,
      unmatchedPlanCount,
    },
    changedCellUniqueIds: prepared.changedEntries
      .map(({ uniqueId }) => uniqueId)
      .filter(Boolean),
  };
};

/** 실제로 값이 들어갈 셀만 단위 제약 대상이다 (비워두는 합계 셀은 제외) */
const getPairedMonetaryUnitDivisors = (
  aligned: AlignedGrid<string, HTMLTableCellElement>,
): number[] => {
  const divisors = new Set<number>();
  for (const { target } of aligned.pairs) {
    if (
      getIxdSchemaValueKind(target.payload.dataset.schemaType) !== "MONETARY"
    ) {
      continue;
    }
    divisors.add(getTargetUnitDivisor(target.payload));
  }
  return [...divisors];
};

function getTargetUnitDivisor(cell: HTMLTableCellElement): number {
  const divisor = Number(cell.dataset.unitDivisor);
  return Number.isFinite(divisor) && divisor > 0 ? divisor : 1;
}

/** 정렬 실패 원인을 dev 콘솔에 남긴다 — 표 개수·행/열 수·합계 표시 중 무엇이 어긋났는지 */
const logMismatch = (reason: string, detail: Record<string, unknown>) => {
  if (!import.meta.env.DEV) return;
  console.warn(`[paste-match] ${reason}`, detail);
};

const describeGrid = (grid: ValueGrid<unknown>) => ({
  rows: grid.length,
  rowWidths: [...new Set(grid.map((row) => row.length))],
  totalMap: grid
    .map((row) => row.map((cell) => (cell.isAxisTotal ? "T" : ".")).join(""))
    .slice(0, 12),
});
interface SourceTargetValuePair {
  sourceValue: string;
  targetRawValue: string;
  schemaType?: string | null;
  unitDivisor?: number;
  negated?: boolean;
  decimalDigits?: number | null;
}

/**
 * 실제 붙여넣기 핸들러와 같은 값 정규화 규칙으로 원본/대상 값이 같은지 확인한다.
 * 화면 문자열만 비교하면 쉼표·음수 표기·단위가 같은 값을 다르다고 볼 수 있다.
 */
export const isSourceValueSameAsTarget = ({
  sourceValue,
  targetRawValue,
  schemaType,
  unitDivisor = 1,
  negated = false,
  decimalDigits,
}: SourceTargetValuePair): boolean => {
  if (sourceValue.trim() === "") {
    return targetRawValue.trim() === "";
  }

  const kind = getIxdSchemaValueKind(schemaType);
  const isMonetary = kind === "MONETARY";
  const normalizedSource = normalizeEditedCellValue({
    schemaType,
    inputValue: sourceValue,
    unitDivisor,
    displayValueMode: isMonetary ? "unit-applied" : "raw",
    // 원본은 DSD 표시값이라 negated 표시 부호를 담고 있다 — 숫자 kind 전체 공통
    displayUsesNegatedSign: isNumericKind(kind),
    negated,
    decimalDigits,
  });
  if (!normalizedSource.ok) return false;

  const validatedTarget = validateStoredCellValue({
    schemaType,
    storedValue: targetRawValue,
  });
  const comparableTarget = validatedTarget.ok
    ? validatedTarget.normalizedValue
    : targetRawValue;

  return comparableTarget === normalizedSource.storedValue;
};

/**
 * 붙여넣기가 실제로 쓰는 짝(aligned.pairs)만 비교한다. 짝이 없는 합계 셀
 * (sourceOnlyTotals/targetOnlyTotals)은 pasteAlignedValues가 건드리지 않으므로
 * 값 동일 판정에서도 제외한다.
 */
// const doPairValuesMatch = ({ aligned }: TablePair): boolean =>
//   aligned.pairs.every(({ source, target }) => {
//     const cell = target.payload;
//     const decimalDigits = cell.dataset.decimalDigits
//       ? Number(cell.dataset.decimalDigits)
//       : NaN;

//     return isSourceValueSameAsTarget({
//       sourceValue: source.payload,
//       targetRawValue: cell.dataset.rawValue ?? "",
//       schemaType: cell.dataset.schemaType,
//       unitDivisor: getTargetUnitDivisor(cell),
//       negated: cell.hasAttribute("data-negated"),
//       decimalDigits: Number.isFinite(decimalDigits) ? decimalDigits : null,
//     });
//   });

/** 값 반영에 성공한 셀 중 하나라도 반영 직후 값에서 달라졌는지 확인한다. */
export const hasAppliedCellValueChanged = (
  snapshot: AppliedCellValueSnapshot,
  targetRoot: HTMLElement,
): boolean =>
  Object.entries(snapshot).some(([uniqueId, appliedValue]) => {
    const cell = targetRoot.ownerDocument.getElementById(uniqueId);
    // 가상화로 잠시 DOM에서 빠진 것은 값 수정으로 보지 않는다.
    if (!cell || !targetRoot.contains(cell)) return false;
    return (cell.getAttribute("data-raw-value") ?? "") !== appliedValue;
  });

export const PasteMatchButton = ({
  targetContainerRef,
  session,
  plan,
  workbookTableId,
  workbookRoleKey,
  onPlanCoverageChange,
  onPasteResult,
  applyLock,
  notAppliedSummary,
  notAppliedCount = 0,
  mixedScaleWarning,
  unitMismatchLines,
  periodEndNotice,
  threeMonthAccMismatch,
  taggedPeriodMismatch,
}: PasteMatchButtonProps) => {
  const [coverage, setCoverage] = useState<PasteMatchPlanCoverage>(
    INITIAL_PLAN_COVERAGE,
  );
  // 측정 콜백은 MutationObserver 등록 effect의 의존성이다. 잠금이 바뀔 때마다
  // 옵저버를 다시 붙이면 불필요한 재측정이 도니 ref로만 읽는다. 읽는 쪽이
  // rAF 콜백(=commit 이후)이라 effect로 갱신해도 최신값을 본다.
  const applyLockRef = useRef(applyLock);
  useEffect(() => {
    applyLockRef.current = applyLock;
  }, [applyLock]);
  const skipLockComparisonOnNextMeasureRef = useRef(false);

  const isApplyLocked = !!applyLock.snapshot;
  const isActionable = coverage.status === "ready" && !isApplyLocked;
  // 자동합계가 바뀐 표는 새 plan이 올 때까지 이전 값을 들고 있다. 버튼은 막지 않고
  // 툴팁으로만 알린다 — 지금 누르면 이전 plan의 값이다. 갱신 중이라는 표시 자체는
  // 자동계산 토글 옆의 "반영 중..."이 맡는다(WXBRL-2664).
  const isPlanRefreshing = useIsWorkbookPlanRefreshing(
    workbookTableId,
    workbookRoleKey,
  );

  useEffect(() => {
    onPlanCoverageChange?.(coverage);
  }, [coverage, onPlanCoverageChange]);

  const applyMeasuredCoverage = useCallback(
    ({ coverage: next }: PlanCoverageMeasurement) => {
      setCoverage(next);
      if (skipLockComparisonOnNextMeasureRef.current) {
        skipLockComparisonOnNextMeasureRef.current = false;
        return;
      }
      // 반영에 성공한 셀 중 하나가 달라졌을 때만 잠금을 푼다. 실패 셀이나
      // 같은 표의 다른 셀 수정은 영향을 주지 않는다.
      const { snapshot, onRelease } = applyLockRef.current;
      if (!snapshot || !targetContainerRef.current) return;
      if (hasAppliedCellValueChanged(snapshot, targetContainerRef.current)) {
        onRelease();
      }
    },
    [targetContainerRef],
  );

  // 표는 별도 렌더러가 그리므로 마운트 시점이 이 버튼과 같지 않다. 최초 측정에
  // 더해 subtree 변경을 관찰해 늦게 그려지거나 다시 그려진 표도 반영한다.
  useEffect(() => {
    const target = targetContainerRef.current;
    if (!target) return;

    let frameId: number | null = null;
    const measure = () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        applyMeasuredCoverage(
          measurePlanCoverage(target, plan, workbookTableId),
        );
      });
    };

    const observer = new MutationObserver(measure);
    observer.observe(target, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        "data-raw-value",
        "data-schema-type",
        "data-readonly",
        ATTR.cellIdentityKey,
      ],
    });

    measure();

    return () => {
      observer.disconnect();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [applyMeasuredCoverage, plan, workbookTableId, targetContainerRef]);

  const applyPreparedValues = (
    prepared: {
      batch: CellEditBatchEntry[];
      changedEntries: readonly ApplyEntryRef[];
      failures: readonly { uniqueId: string }[];
    },
    allEntries: readonly ApplyEntryRef[],
    {
      detail,
      unmatchedPlanCount,
    }: {
      detail: string;
      /** 이 표 계획에 있는데 대응 셀을 못 찾아 아예 시도조차 못한 항목 수 */
      unmatchedPlanCount: number;
    },
  ) => {
    // 연동 셀 충돌로 제외된 batch 인덱스. 제외된 셀만 실패로 표시하고 나머지는 반영된다.
    const rejectedIndexes = new Set(
      prepared.batch.length > 0
        ? session.setAppliedCellValues(prepared.batch)
        : [],
    );
    const appliedCount = prepared.batch.length - rejectedIndexes.size;
    const applied = appliedCount > 0;
    const description = buildApplyToastDescription(detail, mixedScaleWarning);

    let failedCellUniqueIds = [
      ...prepared.failures.map(({ uniqueId }) => uniqueId),
      ...[...rejectedIndexes].map(
        (index) => prepared.changedEntries[index]?.uniqueId ?? "",
      ),
    ];
    failedCellUniqueIds = [...new Set(failedCellUniqueIds)].filter(Boolean);
    const entryByUniqueId = new Map(
      allEntries.map((entry) => [entry.uniqueId, entry.element]),
    );
    const failedCellRawValues = Object.fromEntries(
      failedCellUniqueIds.map((uniqueId) => [
        uniqueId,
        entryByUniqueId.get(uniqueId)?.getAttribute("data-raw-value") ?? "",
      ]),
    );
    if (!applied && failedCellUniqueIds.length > 0) {
      // 값 변경이 하나도 없어도 실패 표시만 독립적으로 되돌릴 수 있게 한다.
      session.pushUndoMarker();
    }

    if (applied || failedCellUniqueIds.length > 0) {
      // 반영 직후 상태를 잠금 스냅샷으로 남긴다. 실패 결과만 있는 경우에도
      // 재실행을 잠그되(빈 스냅샷), 성공 셀이 없으므로 잠금이 풀리지는 않는다.
      const appliedCellSnapshot = applied
        ? Object.fromEntries(
            prepared.changedEntries
              .map(
                ({ uniqueId }, index) =>
                  [uniqueId, prepared.batch[index]?.newValue ?? ""] as const,
              )
              .filter((_, index) => !rejectedIndexes.has(index)),
          )
        : {};
      const appliedIdentityKeys = prepared.batch
        .filter((_, index) => !rejectedIndexes.has(index))
        .flatMap(({ identityKey }) => (identityKey ? [identityKey] : []));
      onPasteResult?.({
        failedCellUniqueIds,
        failedCellRawValues,
        appliedCellSnapshot,
        appliedIdentityKeys,
      });
      // DOM 갱신이 남은 경우(applied)는 첫 measure에서 비교를 건너뛰고
      // 그 다음 변경부터 성공 셀만 비교한다.
      if (applied) skipLockComparisonOnNextMeasureRef.current = true;
    }

    /*
     * "모두 반영"은 이 표에서 안 들어간 항목이 하나도 없을 때만 쓴다.
     * 사용자 눈에는 세 가지가 똑같이 "안 들어간 것"이라 모두 더한다.
     *   - 반영 실패: 읽기 전용 셀·연동 자리 충돌 (failedCellUniqueIds)
     *   - 계획에는 있는데 대응 셀을 못 찾음 (unmatchedPlanCount)
     *   - 서버가 IXD 에서 자리를 못 찾아 계획에서 뺌 (notAppliedCount prop)
     * 이미 같은 값이라 건드리지 않은 셀은 세지 않는다 — 그건 이미 맞는 값이다.
     */
    const notApplied =
      failedCellUniqueIds.length + unmatchedPlanCount + notAppliedCount;

    if (applied || notApplied > 0) {
      toastApplyResult({ applied: appliedCount, notApplied, description });
    } else {
      toast.info("변경할 값이 없습니다.");
    }
  };

  const handleClick = () => {
    const target = targetContainerRef.current;
    if (!target || plan.status !== "ready") return;

    // 렌더 이후 DOM이 변했을 수 있으므로 클릭 시점에 다시 모은다.
    const plannedValues =
      (workbookTableId
        ? plan.index.valuesByWorkbookTableId.get(workbookTableId)
        : undefined) ?? EMPTY_PLANNED_VALUES;
    const entries = collectPlanEntries(target, plannedValues);
    // 0건이어도 센다 — 눌렀는데 채워진 게 없었던 경우가 퍼널에서 보여야 한다
    logIxdEditorAction("값 반영", {
      workbookTableId,
      matchedCellCount: entries.length,
    });
    if (entries.length === 0) {
      toast.info("태깅이 일치하는 셀이 없습니다.");
      return;
    }
    // coverage 가 아니라 클릭 시점의 DOM 으로 다시 센다 — 측정 이후 표가 바뀌었을 수 있다.
    const unmatchedPlanCount = Math.max(0, plannedValues.size - entries.length);

    applyPreparedValues(prepareIxdStoredValues(entries), entries, {
      unmatchedPlanCount,
      // 반영 후에도 추정 건수는 남긴다 — 토스트가 사라지기 전에 확인 대상이
      // 있었다는 사실 자체를 알려야 한다.
      detail:
        coverage.ordinalCount > 0
          ? `${coverage.ordinalCount}개는 표 안 위치로 추정한 값입니다. 확인해주세요.`
          : "",
    });
  };

  const tooltipText = isApplyLocked
    ? "값 반영을 완료했습니다. 반영된 셀을 수정하면 다시 반영할 수 있습니다"
    : isPlanRefreshing
      ? "합계 자동계산 변경을 반영한 값을 다시 받고 있습니다. 지금 반영하면 이전 값이 들어갑니다"
      : coverage.status === "checking"
        ? "반영할 값을 확인하고 있습니다"
        : coverage.status === "plan-error"
          ? "값 동기화 정보를 불러오지 못했습니다"
          : coverage.status === "join-failed"
            ? "계획된 값을 이 표의 셀과 연결하지 못했습니다"
            : coverage.status === "no-match"
              ? "이 표와 태깅이 일치하는 값이 없습니다"
              : coverage.status === "values-match"
                ? "모든 항목 값이 이미 일치합니다"
                : "DSD 워크북 값을 IXD에 반영";

  /*
   * 들어가는 값이 아니라 "안 들어가는 것"만 말한다.
   *
   * 몇 개가 채워지는지는 누른 뒤 화면에서 바로 보이지만, 안 들어간 것은
   * 말해주지 않으면 모른다. 건수는 서버 계획의 failures에서 오므로 워크북에
   * 애초에 값이 없던 셀은 세지 않는다 — 실제로 손봐야 할 양이다.
   *
   * 어느 셀인지는 셀 표식이, 그 셀의 상세 사유는 셀 패널이 맡는다.
   */
  const tooltipSubLines = [
    ...(coverage.status === "join-failed"
      ? [`계획된 ${coverage.unmatchedPlanCount}개 항목이 연결되지 않았습니다`]
      : []),
    ...(notAppliedSummary ?? []),
    ...(unitMismatchLines ?? []),
    ...(periodEndNotice ? [periodEndNotice.message] : []),
    ...(threeMonthAccMismatch ? [threeMonthAccMismatch] : []),
    ...(taggedPeriodMismatch ? [taggedPeriodMismatch] : []),
    ...(mixedScaleWarning
      ? [
          mixedScaleWarning.text,
          ...mixedScaleWarning.rows.map(
            ({ label, lineItems }) => `${label}: ${lineItems}`,
          ),
        ]
      : []),
  ];
  /*
   * 단위·기간 기준이 어긋난 표면 버튼 모서리에 주의 점을 단다 — DSD 워크북과 IXD 단위나
   * 기말 시점 표/기간 표나 3개월·누적 열 구성이 다르거나, 같은 외화가 다른 배율로 섞였을 때만. 툴팁은 hover 해야 보이므로 점이 먼저 눈을 끈다.
   * 이미 반영해 잠긴 버튼에는 달지 않는다.
   */
  const showCautionBadge =
    isActionable &&
    ((unitMismatchLines?.length ?? 0) > 0 ||
      periodEndNotice?.kind === PeriodEndNoticeKind.MISMATCH ||
      !!threeMonthAccMismatch ||
      !!taggedPeriodMismatch ||
      !!mixedScaleWarning);
  const tooltipLabel =
    tooltipSubLines.length > 0 ? (
      <div className="flex flex-col gap-0.5 whitespace-nowrap">
        <span>{tooltipText}</span>
        {tooltipSubLines.map((line) => (
          <span key={line} className="caption-2-regular text-static-white/72">
            {line}
          </span>
        ))}
      </div>
    ) : (
      tooltipText
    );
  // 줄마다 끝 마침표를 떼고 잇는다 — 이미 "."로 끝나는 문구가 있으면 ".."로 읽힌다.
  const tooltipAriaLabel = [tooltipText, ...tooltipSubLines]
    .map((line) => line.replace(/\.$/, ""))
    .join(". ");

  const button = (
    <button
      type="button"
      disabled={!isActionable}
      onClick={handleClick}
      aria-label={tooltipAriaLabel}
      // 반영할 값을 세는 중. 스피너를 버튼에서 걷어냈어도(WXBRL-2664) "지금 계산
      // 중"이라는 사실 자체는 남겨야 보조기술이 읽고 테스트가 신호로 쓸 수 있다.
      aria-busy={coverage.status === "checking"}
      // Figma Button/Solid/Primary (7160:520390): 28px 높이, 좌우 8, 간격 4, radius 6,
      // Label 2/Medium, 아이콘이 글자 앞
      className={cn(
        "relative flex h-7 shrink-0 items-center gap-1 rounded-small bg-blue-500 px-2 label-2-medium text-static-white transition-colors disabled:opacity-24",
        isActionable
          ? "hover:bg-blue-500/88 cursor-pointer"
          : "cursor-not-allowed",
      )}
    >
      <ValueApplyIcon />
      <span>값 반영</span>
      {/* 버튼 오른쪽 위 모서리 바깥으로 걸쳐 둔다. 버튼에 relative 가 없으면 감싼 표 헤더 바 모서리로
          날아가 버튼과 떨어져 보인다(plan 전환 이전 구현이 그랬다). */}
      {showCautionBadge && (
        <span
          aria-hidden="true"
          data-apply-caution-badge
          className="absolute -right-1.5 -top-1.5 size-3 rounded-full border-2 border-background-base bg-status-caution"
        />
      )}
    </button>
  );

  // 값 셀이 없는 블록에는 버튼 자리를 만들지 않는다.
  if (coverage.status === "no-cells") return null;

  return (
    <Tooltip label={tooltipLabel} side="bottom" sideOffset={8}>
      <span className="inline-flex">{button}</span>
    </Tooltip>
  );
};
